import { Snackbar } from '@mui/material';
import { Alert } from '@mui/material';
import { useFirebase } from "containers/FirebaseContext";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { chunk, filter, find, get, isEqual, shuffle } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useUsersByClass } from "store/userClasses/selectors";
import { getClassUsers } from "store/userClasses/thunks";
import { useCurrentUser } from "store/users/selectors";
import { revokeTrafficModal } from "store/wheel/actions";
import { useTraffic } from "store/wheel/selectors";
import { updateGroup } from "store/widgetWheel/actions";
import { useWidgetGroup } from "store/widgetWheel/selectors";
import { useUserWidgetsConfig } from "utils/methods/firebaseUserWidgets";
import { useCollectionWidgets } from "utils/methods/firebaseWidgets";
import { v4 as uuid } from "uuid";
import WidgetEditModal from "../../../modules/widget/widgetEditModal";
import WidgetGroupComponent from "./widgetGroupComponent";
import * as Sentry from "@sentry/react";

const GroupWidget = ({ sharableWidget, currentClass }) => {
  const dispatch = useDispatch();
  // ** States :-
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loader, setLoader] = useState({ type: "", value: false });
  const [currentClasId, setCurrentClassId] = useState(currentClass?.id);
  const [toastMessage, setToastMessage] = useState({
    open: false,
    message: "",
  });
  const [fetchAllStudents, setFetchAllStudents] = useState([]);

  // ** use-ref
  const isInitialMount = useRef(true);

  // ** custom hooks :-
  const groupWidgetData = useWidgetGroup();
  const [trafficModal] = useTraffic();
  const usersByClass = useUsersByClass();
  const { firestore } = useFirebase();
  const currentUser = useCurrentUser();
  const { createCollectionWidgets } = useCollectionWidgets();
  const { createUserWidgetsConfig } = useUserWidgetsConfig();

  // ** memo declaration :-
  const groupWidget = useMemo(() => groupWidgetData, [groupWidgetData]);

  const trafficData = useMemo(
    () => trafficModal?.widget_menu ?? [],
    [trafficModal]
  );

  const filterByWidget = useMemo(
    () => find(trafficData, (item) => item?.menu === "new_group"),
    [trafficData]
  );

  const filterShareGroup = useMemo(
    () => find(sharableWidget, (obj) => obj?.widget_type === "new_group"),
    [sharableWidget]
  );

  const sharedData = useMemo(() => filterShareGroup?.data, [filterShareGroup]);

  const widgetGroupOpen = useMemo(
    () =>
      get(currentUser, "role") === "teacher"
        ? filterByWidget?.view && filterByWidget?.menu === "new_group"
        : filterShareGroup?.id,
    [filterByWidget, filterShareGroup, currentUser]
  );

  const getFetchStudent = async () => {
    try {
      if (!widgetGroupOpen) return;
      const students = await getClassUsers(currentClass?.id);
      const filterStudent =
        filter(students, (x) => x.role === "student" && x.active &&x?.userclass?.active) || [];
      setFetchAllStudents(filterStudent);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    getFetchStudent();
  }, [currentClass?.id, widgetGroupOpen]);

  const students = useMemo(() => fetchAllStudents || [], [fetchAllStudents]);

  const checkStudentPerGroup = useMemo(() => {
    const checkStudentGroup =
      !groupWidget?.studentPerGroup ||
      groupWidget?.studentPerGroup === "" ||
      groupWidget?.studentPerGroup === 0 ||
      groupWidget?.studentPerGroup === "0";

    return checkStudentGroup;
  }, [groupWidget?.studentPerGroup]);

  // ** Functions & Effect's:-
  useEffect(() => {
    if (widgetGroupOpen) {
      document.body.classList.add("body-overflow-hidden");
    }

    return () => {
      document.body.classList.remove("body-overflow-hidden");
    };
  }, [widgetGroupOpen]);

  useEffect(() => {
    const getCheck = async () => {
      if (
        (filterShareGroup && !filterShareGroup?.status) ||
        filterShareGroup === undefined
      ) {
        await dispatch(
          updateGroup({
            generatedGroup: [],
            studentPerGroup: 0,
          })
        );
      }
    };
    getCheck();
  }, [widgetGroupOpen]);

  useEffect(() => {
    const getSharedData = async () => {
      if (
        filterShareGroup?.id &&
        filterShareGroup?.status &&
        filterShareGroup?.data
      ) {
        await dispatch(
          updateGroup({
            generatedGroup: JSON.parse(sharedData?.generatedGroup || []),
            studentPerGroup: sharedData?.studentPerGroup || 0,
          })
        );
        return;
      }
    };
    getSharedData();
  }, [
    currentClass.id,
    currentUser,
    usersByClass[currentClass.id],
    sharableWidget,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      filterShareGroup &&
      filterShareGroup?.status &&
      !isEqual(
        JSON.parse(sharedData?.generatedGroup),
        groupWidget?.generatedGroup
      )
    ) {
      const data = {
        ...filterShareGroup,
        data: {
          generatedGroup: JSON.stringify(groupWidget?.generatedGroup) || [],
          studentPerGroup: groupWidget?.studentPerGroup,
        },
      };

      udpateSharedData(data);
    }
  }, [groupWidget]);

  const getUsers = useCallback(() => {
    if (students) {
      const user = students?.map((data) => {
        return {
          userName: `${data.firstName || ""} ${data.lastName || ""}`,
          id: data?.id,
          role: data?.role,
        };
      });

      return user || [];
    }
    return [];
  }, [currentClass, usersByClass, students]);

  useEffect(() => {
    let unsubscribe = () => {};
    listenToWidgetUserConfig(get(currentClass, "id", "")).then(
      (unsubscribeFunc) => {
        unsubscribe = unsubscribeFunc;
      }
    );
    return () => {
      unsubscribe();
    };
  }, [
    firestore,
    currentClass.id,
    currentUser,
    usersByClass[currentClass.id],
    filterByWidget,
    students
  ]);

  const listenToWidgetUserConfig = async (classID) => {
    if (!classID || !currentUser || filterByWidget?.menu !== "new_group") {
      return () => {};
    }
    const fifteenHoursAgo = new Date();
    fifteenHoursAgo.setHours(fifteenHoursAgo.getHours() - 12);

    const collectionRef = collection(
      firestore,
      `widgets_user_config/${classID}/widgets`
    );
    const q = query(
      collectionRef,
      where("id", "==", "new_group"),
      where("modified_on", ">=", fifteenHoursAgo)
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        let sharableData = null;
        querySnapshot.forEach((doc) => {
          sharableData = { id: doc.id, ...doc.data() };
        });
        if (
          !!sharableData &&
          sharableData?.user_data?.length !== 0 &&
          sharableData?.selectedUser?.length !== 0
        ) {
          const user = await getUsers();
          if (user?.length > 0) {
            const allUserId = user?.map((ele) => ele.id) || [];
            const filteredUser =
              sharableData?.user_data?.filter(
                (ele) => !allUserId?.includes(ele.id)
              ) || [];
            const payload = [...user, ...filteredUser];
            await dispatch(
              updateGroup({
                users: payload || [],
                selectedUser: sharableData?.selectedUser,
              })
            );
          } else {
            await dispatch(
              updateGroup({
                users: sharableData?.user_data,
                selectedUser: sharableData?.selectedUser || [],
              })
            );
          }
        } else {
          const user = await getUsers();
          await dispatch(
            updateGroup({ users: shuffle(user), selectedUser: user })
          );
        }
      },
      (error) => {
        console.error("Error listening to widgets user config changes:", error);
      }
    );

    return unsubscribe;
  };

  const handlerShareGroup = () => {
    const isGenerated = groupWidget?.generatedGroup?.length > 0;
    if (!filterShareGroup?.status && (checkStudentPerGroup || !isGenerated)) {
      setToastMessage({
        open: true,
        message: "The group has not been generated yet.",
      });
      return;
    }
    if (!filterShareGroup?.id && isGenerated && !filterShareGroup?.status) {
      shareGroup();
      return;
    } else {
      unshareGroup();
    }
  };

  const shareGroup = () => {
    const shareData = {
      id: uuid(),
      class_id: currentClass?.id,
      created_by: currentUser?.id,
      created_at: new Date(),
      widget_type: "new_group",
      status: true,
      data: {
        generatedGroup: JSON.stringify(groupWidget?.generatedGroup),
        studentPerGroup: groupWidget?.studentPerGroup,
      },
    };
    createCollectionWidgets(shareData);
  };

  const unshareGroup = () => {
    const data = {
      ...filterShareGroup,
      status: false,
      data: {},
      message: "group",
    };
    createCollectionWidgets(data);
  };

  const udpateSharedData = (data) => {
    createCollectionWidgets(data);
  };

  const onClose = async () => {
    if(filterShareGroup?.status){
      await unshareGroup();
    }
    await dispatch(revokeTrafficModal(filterByWidget?.id));
    if (
      (filterShareGroup && !filterShareGroup?.status) ||
      filterShareGroup === undefined ||
      !filterShareGroup
    ) {
      await dispatch(
        updateGroup({
          generatedGroup: [],
          studentPerGroup: 0,
        })
      );
    }
  };

  const handleOpenEditModal = (value) => {
    setOpenEditModal(value);
  };

  const resetAllList = async () => {
    setLoader({ type: "reset-list", value: true });
    try {
      const WidgetsData = {
        id: "new_group",
        modified_on: new Date(),
        user_data: [],
        selectedUser: [],
      };
      const users = await getUsers();
      await createUserWidgetsConfig(WidgetsData, currentClass?.id);
      await dispatch(
        updateGroup({ users: shuffle(users), selectedUser: users })
      );
      handlerGenerateStudentGroup(users);
      handleOpenEditModal(false);
    } catch (error) {
      Sentry.captureException(`372 Error during resetAllList: ${error}`);
      console.log(error);
    } finally {
      setLoader({ type: "", value: false });
    }
  };

  const handlerDone = async (users, selectedUser, action) => {
    setLoader({ type: "save-loader", value: true });
    try {
      const WidgetsData = {
        id: "new_group",
        modified_on: new Date(),
        user_data: users,
        selectedUser: selectedUser,
      };
      await createUserWidgetsConfig(WidgetsData, currentClass?.id);
      if (!checkStudentPerGroup) {
        await handlerGenerateStudentGroup(selectedUser);
      }
      if (action === "custom-done") {
        return;
      }
      handleOpenEditModal(false);
    } catch (error) {
      Sentry.captureException(`373 Error during handlerDone: ${error}`);
      console.log(error);
    } finally {
      setLoader({ type: "", value: false });
    }
  };

  const handlerGenerateStudentGroup = async (selectedUser) => {
    if (checkStudentPerGroup) {
      setToastMessage({
        open: true,
        message: "Students per group field cannot be empty.",
      });
      return;
    }
    const users = selectedUser || groupWidget?.selectedUser;
    const generatedGroup =
      chunk(shuffle(users), groupWidget?.studentPerGroup) || [];
    await dispatch(updateGroup({ generatedGroup }));
  };

  const handleUserNewAdded = async (newGeneratedGroup, newUser) => {
    if (
      newUser?.firstName?.trim()?.length === 0 ||
      newUser?.lastName?.trim()?.length === 0
    ) {
      setToastMessage({
        open: true,
        message: "Either a first name or a last name is required.",
      });
      return;
    }
    try {
      const userPayload = {
        id: uuid(),
        role: "student",
        userName: `${newUser?.firstName || ""} ${newUser?.lastName || ""}`,
      };
      const users = [...groupWidget?.users, userPayload];
      const selectedUser = [...groupWidget?.selectedUser, userPayload];
      await dispatch(
        updateGroup({ users, selectedUser, generatedGroup: newGeneratedGroup })
      );
    } catch (error) {
      Sentry.captureException(`374 Error during handleUserNewAdded: ${error}`);
      console.log(error);
    }
  };

  const resetGroup = async () => {
    if (checkStudentPerGroup && groupWidget?.generatedGroup?.length === 0) {
      setToastMessage({
        open: true,
        message: "The group has not been generated yet.",
      });
      return;
    }
    await dispatch(updateGroup({ generatedGroup: [], studentPerGroup: 0 }));
  };

  const onChangePerStation = async (value) => {
    await dispatch(updateGroup({ studentPerGroup: value }));
  };

  const updateGeneratedGroup = async (groupData) => {
    await dispatch(updateGroup({ generatedGroup: groupData }));
  };

  const handleClose = () => {
    setToastMessage({ open: false, message: "" });
  };

  return (
    <div className={`${widgetGroupOpen ? "disable-key-navigation" : ""}`}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={toastMessage.open}
        autoHideDuration={2000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error">
          <strong>ALERT:</strong> {toastMessage.message}
        </Alert>
      </Snackbar>
      {widgetGroupOpen && (
        <WidgetGroupComponent
          states={{
            currentUser,
            shareStatus: filterShareGroup?.status,
            perGroupStudent: groupWidget?.studentPerGroup,
            generatedGroup: groupWidget?.generatedGroup || [],
          }}
          funcObj={{
            onClose,
            handleOpenEditModal,
            handlerGenerateStudentGroup,
            onChangePerStation,
            handleUserNewAdded,
            resetGroup,
            handlerShareGroup,
            updateGeneratedGroup,
          }}
        />
      )}
      <WidgetEditModal
        open={openEditModal}
        close={() => handleOpenEditModal(false)}
        users={groupWidget?.users}
        selectedUser={groupWidget?.selectedUser}
        loader={loader}
        handlerDone={handlerDone}
        resetAllList={resetAllList}
      />
    </div>
  );
};

export default GroupWidget;
